import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck, Bot, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";


/**
 * Rotating promo banner with per-campaign A/B testing.
 *
 * - Three independent campaigns rotate (one shown per visitor, sticky):
 *     m365-assessment, it-strategy, ai-readiness
 * - Each campaign has two variants (A / B) for headline copy testing.
 * - Events (impression / click / dismiss) are tracked in promo_banner_events
 *   keyed by `banner_key` (campaign) + `variant`.
 * - Dismissals expire after 10 days so the banner can reappear.
 */

type VariantId = "A" | "B";

interface BannerVariant {
  id: VariantId;
  icon: typeof ShieldCheck;
  headline: string;
  subline: string;
  cta: string;
}

interface BannerCampaign {
  key: string; // banner_key in analytics
  link: string;
  hiddenOn: string[]; // route prefixes where this campaign should not show
  variants: [BannerVariant, BannerVariant];
}

const CAMPAIGNS: BannerCampaign[] = [
  {
    key: "m365-assessment-v1",
    link: "https://universeit.com/m365-security-assessment#assessment",
    hiddenOn: [],

    variants: [
      {
        id: "A",
        icon: ShieldCheck,
        headline: "Free M365 Security Assessment",
        subline: "Scan your tenant in minutes and get a prioritized remediation plan.",
        cta: "Run free scan",
      },
      {
        id: "B",
        icon: ShieldCheck,
        headline: "Is your Microsoft 365 tenant secure?",
        subline: "Get a free score, benchmark, and 90-day roadmap in under 5 minutes.",
        cta: "Check my score",
      },
    ],
  },
  {
    key: "copilot-assessment-v1",
    link: "https://universeit.com/ai-automation#readiness",

    hiddenOn: ["/m365-success"],
    variants: [
      {
        id: "A",
        icon: Bot,
        headline: "Free Copilot Readiness Assessment",
        subline: "See exactly where Copilot fits in your business — and the use cases worth rolling out first.",
        cta: "Check my Copilot fit",
      },
      {
        id: "B",
        icon: Bot,
        headline: "Ready to put Copilot to work?",
        subline: "Get a free readiness score and the top Copilot use cases for your team in minutes.",
        cta: "See my Copilot score",
      },
    ],
  },
];

const HIDDEN_PREFIXES = ["/admin", "/client-portal", "/proposals"];
const VISITOR_KEY = "promo-visitor-id";
const CAMPAIGN_KEY = "promo-campaign-id";
const ROTATE_INTERVAL_MS = 10_000;

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

const getOrCreateVisitorId = (): string => {
  const existing = safeGet(VISITOR_KEY);
  if (existing) return existing;
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  safeSet(VISITOR_KEY, id);
  return id;
};

const hashString = (input: string): number => {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (h * 31 + input.charCodeAt(i)) >>> 0;
  }
  return h;
};

const pickVariant = (visitorId: string, campaignKey: string): VariantId => {
  return hashString(`${visitorId}:${campaignKey}`) % 2 === 0 ? "A" : "B";
};

const trackEvent = (
  bannerKey: string,
  variant: VariantId,
  eventType: "impression" | "click" | "dismiss",
  visitorId: string,
  path: string,
) => {
  void supabase
    .from("promo_banner_events")
    .insert({
      banner_key: bannerKey,
      variant,
      event_type: eventType,
      visitor_id: visitorId,
      path,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    })
    .then(() => undefined, () => undefined);
};

const PromoBanner = () => {
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const [visitorId, setVisitorId] = useState<string>("");
  const [campaignIndex, setCampaignIndex] = useState<number>(0);
  // Map of banner_key -> promoted variant id (overrides A/B assignment).
  const [promotedVariants, setPromotedVariants] = useState<Record<string, VariantId>>({});
  const [isPaused, setIsPaused] = useState(false);
  const impressionLogged = useRef<string>("");

  // Initialize visitor + advance campaign on every page navigation so all three rotate.
  useEffect(() => {
    const vid = getOrCreateVisitorId();
    setVisitorId(vid);

    const stored = safeGet(CAMPAIGN_KEY);
    let prev = stored ? Number(stored) : NaN;
    if (!Number.isFinite(prev) || prev < 0 || prev >= CAMPAIGNS.length) {
      prev = hashString(`${vid}:campaign`) % CAMPAIGNS.length;
    } else {
      prev = (prev + 1) % CAMPAIGNS.length;
    }
    safeSet(CAMPAIGN_KEY, String(prev));
    setCampaignIndex(prev);
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Auto-rotate campaigns every 10 seconds while the banner is visible.
  useEffect(() => {
    if (!ready || isPaused) return;
    const interval = window.setInterval(() => {
      setCampaignIndex((idx) => {
        const next = (idx + 1) % CAMPAIGNS.length;
        safeSet(CAMPAIGN_KEY, String(next));
        return next;
      });
    }, ROTATE_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [ready, isPaused]);

  // Fetch active promotions so we can serve the winning variant at 100% traffic.
  // Failures are silent — we just fall back to A/B rotation.
  useEffect(() => {
    let cancelled = false;
    void supabase
      .from("promo_banner_promotions")
      .select("banner_key,variant")
      .is("revoked_at", null)
      .then(({ data }) => {
        if (cancelled || !data) return;
        const map: Record<string, VariantId> = {};
        for (const row of data as Array<{ banner_key: string; variant: string }>) {
          if (row.variant === "A" || row.variant === "B") {
            map[row.banner_key] = row.variant;
          }
        }
        setPromotedVariants(map);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Pick the first campaign (starting at the assigned index) that's allowed
  // on this route AND hasn't been dismissed recently. Honor any active promotion.
  const active = useMemo(() => {
    if (!ready || !visitorId) return null;

    if (HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

    for (let i = 0; i < CAMPAIGNS.length; i++) {
      const idx = (campaignIndex + i) % CAMPAIGNS.length;
      const campaign = CAMPAIGNS[idx];
      if (campaign.hiddenOn.some((p) => location.pathname.startsWith(p))) continue;
      // If a winner has been promoted, force that variant. Otherwise, A/B by visitor.
      const variantId: VariantId = promotedVariants[campaign.key] ?? pickVariant(visitorId, campaign.key);
      const variant = campaign.variants.find((v) => v.id === variantId) ?? campaign.variants[0];
      return { campaign, variant };
    }
    return null;
  }, [ready, visitorId, campaignIndex, location.pathname, promotedVariants]);

  // Log a single impression per (campaign+variant+route).
  useEffect(() => {
    if (!active || !visitorId) return;
    const key = `${active.campaign.key}:${active.variant.id}:${location.pathname}`;
    if (impressionLogged.current === key) return;
    impressionLogged.current = key;
    trackEvent(active.campaign.key, active.variant.id, "impression", visitorId, location.pathname);
  }, [active, visitorId, location.pathname]);

  if (!active) return null;

  const { campaign, variant } = active;
  const Icon = variant.icon;

  const handleClick = () => {
    trackEvent(campaign.key, variant.id, "click", visitorId, location.pathname);
  };


  const goTo = (delta: 1 | -1) => {
    const next = (campaignIndex + delta + CAMPAIGNS.length) % CAMPAIGNS.length;
    safeSet(CAMPAIGN_KEY, String(next));
    setCampaignIndex(next);
  };

  return (
    <div
      className="relative bg-gradient-to-r from-primary via-primary-light to-primary text-primary-foreground shadow-lg ring-1 ring-primary-foreground/10 overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setIsPaused(false);
        }
      }}
    >
      {/* Animated shimmer accent */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/2 bg-gradient-to-r from-transparent via-primary-foreground/50 to-transparent blur-sm animate-[shimmer_3.5s_ease-in-out_infinite]"
      />
      {/* Bottom accent stripe */}
      <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-accent via-primary-foreground/60 to-accent" />

      <div className="relative container mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-2 sm:gap-4 py-3 sm:py-5">
          <div className="flex items-center gap-2.5 sm:gap-4 min-w-0 flex-1">
            <span className="flex h-9 w-9 sm:h-11 sm:w-11 flex-shrink-0 items-center justify-center rounded-full bg-primary-foreground/15 ring-1 ring-primary-foreground/30 backdrop-blur-sm">
              <Icon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-sm sm:text-lg leading-tight break-words">
                {variant.headline}
              </p>
              <p className="hidden sm:block text-sm text-primary-foreground/85 truncate">
                {variant.subline}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
            <button
              onClick={() => goTo(-1)}
              aria-label="Previous promotion"
              className="p-1 sm:p-1.5 rounded-full hover:bg-primary-foreground/15 transition-colors"
            >
              <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
            <a
              href={campaign.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClick}
              className="inline-flex items-center gap-1 sm:gap-1.5 rounded-full bg-primary-foreground text-primary px-3 py-1.5 sm:px-5 sm:py-2.5 text-xs sm:text-base font-bold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all whitespace-nowrap"
            >
              {variant.cta}
              <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
            </a>

            <button
              onClick={() => goTo(1)}
              aria-label="Next promotion"
              className="p-1 sm:p-1.5 rounded-full hover:bg-primary-foreground/15 transition-colors"
            >
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoBanner;
